import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { WF } from "../constants/testIds2";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus } from "@phosphor-icons/react";

export default function Projects() {
  const { user, tenantId } = useAuth();
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ code: "", name: "", client_id: "", start_date: "", end_date: "", billable_default: true, description: "" });
  const [err, setErr] = useState("");
  const canManage = ["super_admin", "company_admin", "hr"].includes(user?.role);

  const load = () => {
    const params = user?.role === "super_admin" && tenantId ? { tenant_id: tenantId } : {};
    api.get("/projects", { params }).then(({ data }) => setRows(data));
    api.get("/clients", { params }).then(({ data }) => setClients(data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tenantId, user?.role]);

  const submit = async () => {
    setErr("");
    try {
      const payload = { ...form };
      if (user?.role === "super_admin" && tenantId) payload.tenant_id = tenantId;
      await api.post("/projects", payload);
      toast.success("Project created");
      setOpen(false); setForm({ code: "", name: "", client_id: "", start_date: "", end_date: "", billable_default: true, description: "" });
      load();
    } catch (e) { setErr(formatApiError(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-emerald-700">Workforce</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Projects</h1>
          <p className="text-sm text-zinc-500 mt-2">{rows.length} projects · Employee allocation and timesheets tie back here.</p>
        </div>
        {canManage && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="btn-zenith rounded-sm" data-testid={WF.projectCreateBtn}><Plus size={16} className="mr-1.5" /> New project</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-display text-2xl">Add project</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs">Code *</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className="rounded-sm font-mono-plex" data-testid="project-code-input" /></div>
                <div className="space-y-1"><Label className="text-xs">Client *</Label>
                  <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                    <SelectTrigger className="rounded-sm" data-testid="project-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                    <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-2 space-y-1"><Label className="text-xs">Project name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm" data-testid="project-name-input" /></div>
                <div className="space-y-1"><Label className="text-xs">Start date</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="rounded-sm" /></div>
                <div className="space-y-1"><Label className="text-xs">End date</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="rounded-sm" /></div>
              </div>
              {err && <div className="text-sm text-red-600 border border-red-200 bg-red-50 px-3 py-2 rounded-sm">{err}</div>}
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
                <Button onClick={submit} className="btn-zenith rounded-sm" data-testid={WF.projectSaveBtn}>Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="overflow-x-auto border border-zinc-200 rounded-sm" data-testid={WF.projectsList}>
        <table className="hr-table">
          <thead><tr><th>Code</th><th>Project</th><th>Client</th><th>Start</th><th>End</th><th>Billable</th><th>Status</th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-zinc-500 text-sm">No projects yet.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id}>
                <td className="font-mono-plex text-xs">{r.code}</td>
                <td className="font-medium">{r.name}</td>
                <td>{r.client_name || "—"}</td>
                <td className="font-mono-plex text-xs">{r.start_date || "—"}</td>
                <td className="font-mono-plex text-xs">{r.end_date || "—"}</td>
                <td>{r.billable_default ? <Badge variant="outline" className="rounded-sm bg-emerald-50 text-emerald-800 border-emerald-200">Billable</Badge> : <Badge variant="outline" className="rounded-sm bg-zinc-100 border-zinc-300">Internal</Badge>}</td>
                <td><Badge variant="outline" className="rounded-sm bg-emerald-50 text-emerald-800 border-emerald-200">{r.status}</Badge></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
